/**
 * Vertical Navigation
 *
 * handles navigation:
 *  properties
 *  messages
 *  maintenance
 *  payments
 *  alerts
 *  contacts
 *  analytics
 *  billing
 */

import { memo, useEffect, useState } from 'react';
import includes from 'lodash/includes';

import {
  Divider,
  IconArchive,
  IconArchiveOutline,
  IconBank,
  IconBankOutline,
  IconDollarSignCircle,
  IconDollarSignCircleOutline,
  IconEditForm,
  IconEditFormOutline,
  IconHammer,
  IconHammerOutline,
  IconHouseType,
  IconHouseTypeOutline,
  IconMessage,
  IconMessageOutline,
  IconTrending,
  IconTrendingOutline,
  IconUser,
  IconUserOutline,
  ZillowLogo,
} from '@zillow/constellation';

import { PathsManifest } from '@zg-rentals/paths-manifest';

import { getPaymentsPath } from '../../utils';
import useNavDisplay from '../../hooks/useNavDisplay';
import { registerTrackingEvent } from '../../api';
import { CSTriggerSourceFromHeaderTitlesMap, HeaderTitles } from '../../constants';

import HeaderNavigationClickstreamEvents from '../../models/HeaderNavigationClickstreamEvents';

import AccountMenu from './components/AccountMenu';
import AdaptiveAnchor from '../AdaptiveAnchor';
import LogoZRMStacked from '../Logo/components/LogoZRMStacked';
import NavigationItem from './components/NavigationItem';
import NotificationCenter from '../NotificationCenter';
import PlusIconPropertyMenu from '../PlusIconPropertyMenu';

import {
  StyledAccountContainer,
  StyledLogoContainer,
  StyledLogoZRMStacked,
  StyledMessageGleamContainer,
  StyledNavigationListContainer,
  StyledNotificationCircle,
  StyledVerticalNavigation,
} from './styles';

import type { Props as HeaderNavigationProps } from '../../HeaderNavigation';
import type { NotificationItem } from '../NotificationCenter/interfaces/notificationItem';
import type { User } from '../../interfaces/user';
import { CometUserRoleTypeString } from '@zg-rentals/rental-manager-env-base';

// TODO: find a better place for this declaration
declare global {
  interface Window {
    // eslint-disable-next-line
    browserHistory: any;
  }
}

type Props = Partial<HeaderNavigationProps>;

const VerticalNavigation = (props: Props) => {
  const [isAlertDropdownOpen, setIsAlertDropdownOpen] = useState<boolean>(false);

  /**
   * will pass this to NotificationCenter
   * where the api is called and sync response
   * hasNewNotificationsForNotificationCenter gets remapped to hasNewNotifications
   */
  const [notificationStateValues, setNotificationStateValues] = useState<{
    notifications: Array<NotificationItem>;
    isPopupOpen: boolean;
    hasError: boolean;
    startIndex: number;
    showPagination: boolean;
    showMarkAllAsRead: boolean;
    isFirstTimeLoad: boolean;
    hasNewNotifications: boolean;
  }>({
    notifications: [],
    isPopupOpen: false,
    hasError: false,
    startIndex: 0,
    showPagination: false,
    showMarkAllAsRead: false,
    isFirstTimeLoad: true,
    hasNewNotifications: false,
  });

  const { displays } = useNavDisplay(props);

  const {
    /**
     * altLogo true - zrm logo
     * altLogo false - zillow logo
     */
    altLogo = true,
    brand = 'zillow',
    csrf_token = '',
    pathname = '',
    proxyPath = '',
    reactRouterLink: v4Link,
    userModel = {} as User,
    webSocketInstance = null,
    escapeAllLinks = false,
    zgApi = '',
  } = props;

  const { features = {}, loggedIn, paymentsAcceptedCount, hasSeenNewPaymentsPage } = userModel;
  const { canViewClaimFeedListing: canAccessAddFeedListingsModal = false, canViewOnboardingFlow = false } =
    features as User['features'];

  const {
    displayAnalytics,
    displayMainBilling,
    displayMainLeadManagement,
    displayMainLeads,
    displayMainMaintenanceTracker,
    displayMainMessages,
    displayMainMyProperties,
    displayMainNotificationCenter,
    displayMainPayments,
    displayOnboardingTasks,
    displayUserManagement,
  } = displays;

  // Pass link component from either react-router or next-js
  const Link = v4Link;

  const useAbsolutePaths = !props.useRelativePaths || !props.reactRouterLink;
  const paths = new PathsManifest('/rental-manager', brand, useAbsolutePaths, props.host).getAllPaths();

  const {
    adManagementPage,
    analyticsOverview,
    analyticsPage,
    billingDue,
    billingPage,
    inboxPage,
    leadManagementPage,
    leadsPage,
    maintenanceTrackerPage,
    payments,
    paymentsAggregatePage,
    paymentsGetStartedPage,
    propertiesPage,
    rentPayment,
    rentPaymentPage,
    setupTasks,
    adManagementTabsPage,
    userManagementPage,
  } = paths;

  useEffect(() => {}, [notificationStateValues]);

  const handleLinkClick = (pageClickingTo: string) => {
    if (loggedIn && userModel) {
      const csLink = CSTriggerSourceFromHeaderTitlesMap[pageClickingTo];

      if (pageClickingTo === HeaderTitles.payments && hasSeenNewPaymentsPage === false) {
        registerTrackingEvent(proxyPath, csrf_token, zgApi, 'openNewPaymentsPage');
      }

      if (csLink) {
        HeaderNavigationClickstreamEvents.ClickNavigationLink({ user: userModel, link: csLink });
      } else {
        // eslint-disable-next-line
        console.error('ERROR@handleLinkClick: trigger source does not exist.', csLink);
      }
    }
  };

  const handleClickPropertyMenuItem = (event: React.SyntheticEvent) => {
    // prevent parent navigation
    event.stopPropagation();

    handleLinkClick(HeaderTitles.myProperties);
  };

  const isPropertiesPageActive = includes(pathname, propertiesPage);
  const isPaymentPageActive = includes(pathname, payments) || includes(pathname, rentPayment);
  const displayDivider = displayOnboardingTasks === true;
  const isNonAdvertiser = userModel?.roles?.includes(CometUserRoleTypeString.NON_ADVERTISER_CUSTOMER);

  return (
    <StyledVerticalNavigation data-testid="VerticalNavigation">
      {/* logo */}
      <StyledLogoContainer>
        <AdaptiveAnchor path={propertiesPage} escape={escapeAllLinks} useAbsolutePaths={useAbsolutePaths} link={Link}>
          <StyledLogoZRMStacked>{altLogo ? <LogoZRMStacked /> : <ZillowLogo />}</StyledLogoZRMStacked>
        </AdaptiveAnchor>
      </StyledLogoContainer>

      <StyledNavigationListContainer>
        {/* properties */}
        {loggedIn && displayMainMyProperties && (
          <NavigationItem
            isActive={isPropertiesPageActive}
            title={HeaderTitles.myProperties}
            escape={escapeAllLinks}
            path={propertiesPage}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconHouseType size="sm" />}
            InactiveIcon={<IconHouseTypeOutline size="sm" />}
            TrailingIcon={
              <PlusIconPropertyMenu
                Link={Link}
                canAccessAddFeedListingsModal={canAccessAddFeedListingsModal}
                escapeAllLinks={escapeAllLinks}
                handleNoFeedIconPlusClick={handleLinkClick}
                onClickPropertyMenuItem={handleClickPropertyMenuItem}
                propertiesPage={propertiesPage}
                useAbsolutePaths={useAbsolutePaths}
                userModel={userModel}
              />
            }
            onClick={() => {
              handleLinkClick(HeaderTitles.myProperties);
            }}
          />
        )}

        {/* lead management */}
        {displayMainLeadManagement && (
          <NavigationItem
            isActive={includes(pathname, leadManagementPage)}
            title={HeaderTitles.leadManagement}
            escape={true}
            path={leadManagementPage}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconUser size="sm" />}
            InactiveIcon={<IconUserOutline size="sm" />}
            TrailingIcon={null}
            newTagCookieName="hasSeenLeadManagement"
            showNewTag={!canViewOnboardingFlow}
            onClick={() => {
              handleLinkClick(HeaderTitles.leadManagement);
            }}
          />
        )}

        {/* messages */}
        {displayMainMessages && (
          <NavigationItem
            isActive={includes(pathname, inboxPage)}
            title={HeaderTitles.messages}
            escape={true}
            path={inboxPage}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconMessage size="sm" />}
            InactiveIcon={<IconMessageOutline size="sm" />}
            TrailingIcon={null}
            onClick={() => {
              handleLinkClick(HeaderTitles.messages);
            }}
          />
        )}

        {/* maintenance */}
        {displayMainMaintenanceTracker && (
          <NavigationItem
            isActive={includes(pathname, maintenanceTrackerPage)}
            title={HeaderTitles.maintenanceTracker}
            escape={true}
            path={maintenanceTrackerPage}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconHammer size="sm" />}
            InactiveIcon={<IconHammerOutline size="sm" />}
            TrailingIcon={null}
            onClick={() => {
              handleLinkClick(HeaderTitles.maintenanceTracker);
            }}
          />
        )}

        {/* contacts */}
        {displayMainLeads && (
          <NavigationItem
            isActive={includes(pathname, leadsPage)}
            title={HeaderTitles.leads}
            escape={true}
            path={leadsPage}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconMessage size="sm" />}
            InactiveIcon={<IconMessageOutline size="sm" />}
            TrailingIcon={null}
            onClick={() => {
              handleLinkClick(HeaderTitles.leads);
            }}
          />
        )}

        {/* analytics */}
        {displayAnalytics && (
          <NavigationItem
            isActive={includes(pathname, analyticsPage)}
            title={HeaderTitles.analytics}
            escape={true}
            path={`${analyticsPage}/${analyticsOverview}`}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconTrending size="sm" />}
            InactiveIcon={<IconTrendingOutline size="sm" />}
            TrailingIcon={null}
            onClick={() => {
              handleLinkClick(HeaderTitles.analytics);
            }}
          />
        )}

        {/* billing */}
        {displayMainBilling && (
          <NavigationItem
            isActive={includes(pathname, billingPage)}
            title={HeaderTitles.billing}
            escape={true}
            path={`${billingPage}/${billingDue}`}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconBank size="sm" />}
            InactiveIcon={<IconBankOutline size="sm" />}
            TrailingIcon={null}
            onClick={() => {
              handleLinkClick(HeaderTitles.billing);
            }}
          />
        )}

        {/* user management */}
        {displayUserManagement && (
          <NavigationItem
            isActive={includes(pathname, userManagementPage)}
            title={HeaderTitles.userManagement}
            escape={true}
            path={userManagementPage}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconUser size="sm" />}
            InactiveIcon={<IconUserOutline size="sm" />}
            TrailingIcon={null}
            onClick={() => {
              handleLinkClick(HeaderTitles.userManagement);
            }}
          />
        )}

        {/* ad management */}
        {loggedIn && userModel.isEnterpriseCustomer && (
          <NavigationItem
            isActive={includes(pathname, adManagementTabsPage)}
            title={HeaderTitles.adManagement}
            escape={true}
            path={`${adManagementPage}/${adManagementTabsPage}`}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            InactiveIcon={<IconArchiveOutline size="sm" />}
            ActiveIcon={<IconArchive size="sm" />}
            TrailingIcon={null}
            onClick={() => {
              handleLinkClick(HeaderTitles.adManagement);
            }}
          />
        )}

        {/* payments */}
        {loggedIn && displayMainPayments && (
          <NavigationItem
            isActive={isPropertiesPageActive ? false : isPaymentPageActive}
            title={HeaderTitles.payments}
            escape={true}
            path={getPaymentsPath({
              paymentsAcceptedCount,
              paymentsAggregatePage,
              paymentsGetStartedPage,
              rentPaymentPage,
            })}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconDollarSignCircle size="sm" />}
            InactiveIcon={<IconDollarSignCircleOutline size="sm" />}
            onClick={() => {
              handleLinkClick(HeaderTitles.payments);
            }}
          />
        )}

        {/* alerts */}
        {loggedIn && displayMainNotificationCenter && (
          <NavigationItem
            isActive={false}
            title={HeaderTitles.alerts}
            escape={true}
            path={null}
            link={Link}
            InactiveIcon={
              <NotificationCenter
                brand={brand}
                proxyPath={proxyPath}
                csrf_token={csrf_token}
                isLayoutHorizontal={true}
                isOpen={isAlertDropdownOpen}
                webSocketInstance={webSocketInstance}
                zgApi={zgApi}
                setNotificationStateValues={setNotificationStateValues}
              />
            }
            TrailingIcon={
              notificationStateValues?.hasNewNotifications && (
                <StyledMessageGleamContainer>{<StyledNotificationCircle />}</StyledMessageGleamContainer>
              )
            }
            onClick={(event: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => {
              if (event) {
                // do not navigate anywhere
                event.preventDefault();
                event.stopPropagation();
              }

              handleLinkClick(HeaderTitles.alerts);

              // control the dropdown open state so that the entire item is clickable
              if (isAlertDropdownOpen === false) {
                setIsAlertDropdownOpen(true);
              }

              if (isAlertDropdownOpen === true) {
                // use a slight delay when closing the dropdown
                setTimeout(() => {
                  setIsAlertDropdownOpen(false);
                }, 100);
              }
            }}
          />
        )}

        {displayDivider && (
          <>
            <Divider marginY="sm" />

            {/* onboarding tasks */}
            {displayOnboardingTasks && (
              <NavigationItem
                isActive={includes(pathname, setupTasks)}
                title={HeaderTitles.onboardingTasks}
                escape={true}
                path={`${adManagementPage}/${setupTasks}`}
                link={Link}
                useAbsolutePaths={useAbsolutePaths}
                ActiveIcon={<IconEditForm size="sm" />}
                InactiveIcon={<IconEditFormOutline size="sm" />}
                TrailingIcon={null}
                onClick={() => {
                  handleLinkClick(HeaderTitles.onboardingTasks);
                }}
              />
            )}
          </>
        )}
      </StyledNavigationListContainer>

      <StyledAccountContainer>
        <AccountMenu displays={displays} paths={paths} userModel={userModel} />
      </StyledAccountContainer>
    </StyledVerticalNavigation>
  );
};

export default memo(VerticalNavigation);
